import React, {
	memo,
	useCallback,
	useMemo,
	useState
} from 'react';
import {
	useHistory,
	useParams
} from 'react-router-dom';
import { Tab } from 'semantic-ui-react';

import { log } from '../../../../../services/utils';

import { getCalendarEvent } from '../../../../../api/nora/calendar';

import { useAuthState } from '../../../../../contexts/AuthContext';
import {
	useCalendarState,
	useCalendarAction
} from '../../../../../contexts/CalendarContext';
import { useUIAction } from '../../../../../contexts/UIContext';

import Actions from '../../../../Shared/Actions';
import Alert from '../../../../Alert';
import HelpModal from '../../../../Shared/HelpModal';
import TableRowActions from '../../.././../Table/Cells/TableRowActions';
import multiWordFilterFn from '../../../../Table/filterFunctions/multiWordFilterFn';
import NewAttendeeModal from './NewAttendeeModal';
import PageHeader from '../../../../Shared/PageHeader';
import Photo from '../../../../Table/Cells/Photo';
import Table from '../../../../Table';

const AttendeesTab = () => {
	const history = useHistory();
	const { id } = useParams();

	const { user: { permissions } } = useAuthState();
	const { calendarEvent } = useCalendarState();
	const { setCalendarEvent } = useCalendarAction();
	const {
		setApiErrorAlert,
		setInfobarLoader
	} = useUIAction();

	const [ newAttendeeModal, setNewAttendeeModal ] = useState( false );
	const [ deleteConfirm, setDeleteConfirm ] = useState( false );
	const [ attendeeToDelete, setAttendeeToDelete ] = useState( {} );

	const retrieveCalendarEvent = useCallback( async () => {
		setInfobarLoader( 'Retrieving Event...' );

		try {
			const data = await getCalendarEvent( id );

			setCalendarEvent( data );
		} catch ( error ) {
			setApiErrorAlert( error );
		}

		setInfobarLoader( '' );
	}, [
		id,
		setInfobarLoader,
		setCalendarEvent,
		setApiErrorAlert
	] );

	const onView = useCallback( ( {
		contactID,
		email,
		name
	} ) => {
		// if id = 0 -> set up a navigationState obj with name, email, photoUri
		let initialData = {};

		if ( !contactID ) {
			const [
				firstName,
				lastName
			] = name.split( ' ' );

			initialData = {
				email,
				firstName,
				lastName
			};
		}

		// navigate to contact/:id
		history.push( {
			pathname: `/operations/contacts/${ contactID || 0 }`,
			state: {
				text: '\u2190\u00A0\u00A0Back to Event',
				to: {
					pathname: `/operations/calendar/${ id }`,
					state: { activeIndex: 1 }
				},
				initialData
			}
		} );
	}, [
		history,
		id
	] );

	const onDelete = useCallback( attendee => {
		setAttendeeToDelete( attendee );
		setDeleteConfirm( true );
	}, [] );

	const tableRowActionsCell = useCallback( ( { row } ) => {
		const actions = [
			{
				action: onView,
				componentProps: { color: 'pink' },
				disable: !permissions.viewCalendarEvents.canExecute,
				icon: row.original.contactID ? 'search' : 'add',
				permission: permissions.viewCalendarEvents.isAvailable,
				text: 'View Attendee'
			},
			{
				action: onDelete,
				componentProps: { negative: true },
				disable: !permissions.editCalendarEvents.canExecute,
				icon: 'times',
				permission: permissions.editCalendarEvents.isAvailable,
				text: 'Delete Attendee'
			}
		];

		return (
			<TableRowActions
				actions={ actions }
				row={ row.original }
			/>
		);
	}, [
		onDelete,
		onView,
		permissions
	] );

	// TODO: photoCell

	const PhotoCell = useCallback( ( { row } ) => <Photo photoUri={ row.original.photoUri } />, [] );

	const columns = useMemo( () => [
		{
			accessor: 'photoUri',
			Header: '',
			Cell: PhotoCell,
			filterable: false,
			disableFilters: true,
			disableResizing: true,
			minWidth: 60,
			width: 60
		},
		{
			accessor: 'name',
			Header: 'Name',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			accessor: 'organization',
			Header: 'Organization',
			filter: multiWordFilterFn,
			minWidth: 60
		},
		{
			id: 'id_actions',
			Cell: tableRowActionsCell,
			disableFilters: true,
			disableResizing: true,
			width: 100,
			minWidth: 100,
			maxWidth: 100
		}
	], [
		PhotoCell,
		tableRowActionsCell
	] );

	const addAttendee = useCallback( () => {
		setNewAttendeeModal( true );
	}, [] );

	const actions = useMemo( () => [
		{
			action: addAttendee,
			disable: !permissions.editCalendarEvents.canExecute,
			icon: 'add',
			permission: permissions.editCalendarEvents.isAvailable,
			text: 'Add Attendee'
		}
	], [
		addAttendee,
		permissions
	] );

	const closeNewAttendeeModal = useCallback( () => {
		setNewAttendeeModal( false );
	}, [] );

	const handleDeleteAttendee = useCallback( /* async */ () => {
		try {
			// await deleteAttendee( calendarEvent?.id, attendeeToDelete.id );
		} catch ( error ) {
			setApiErrorAlert( error );
		}
	}, [
		// calendarEvent,
		// attendeeToDelete.id,
		setApiErrorAlert
	] );

	const deleteConfirmMessage = useMemo( () => <span>Are you sure you would like to remove <strong>{ attendeeToDelete.name }</strong> from the list?</span>, [ attendeeToDelete ] );

	const deleteConfirmCloseModal = useCallback( () => setDeleteConfirm( false ), [] );

	return (
		<Tab.Pane as="div">
			{ log( 'AttendeessTable' ) }
			<Actions />
			<PageHeader
				actions={ actions }
				className="table-page-header"
				title={
					<>
						<span>Attendees</span>
						<HelpModal
							id={ 25 }
							triggerStyles={ { verticalAlign: 'top' } }
						/>
					</>
				}
			/>
			<div className="table-container">
				{ calendarEvent?.attendees.count
					? <Table
						columns={ columns }
						data={ calendarEvent.attendees.attendees }
						showCount={ calendarEvent.attendees.count > 10 }
						showEmptyRows={ false }
						showPagination={ calendarEvent.attendees.count > 10 }
					/>
					: <p>No records found.</p>
				}
			</div>
			<NewAttendeeModal
				closeModal={ closeNewAttendeeModal }
				open={ newAttendeeModal }
				retrieveCalendarEvent={ retrieveCalendarEvent }
			/>
			<Alert
				action={ handleDeleteAttendee }
				name="attendee-list-delete-confirm-alert"
				message={ deleteConfirmMessage }
				open={ deleteConfirm }
				closeModal={ deleteConfirmCloseModal }
				type="delete"
			/>
		</Tab.Pane>
	);
};

export default memo( AttendeesTab );

